import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ScreenBackdrop } from './ScreenBackdrop';
import { BottomSheet } from './BottomSheet';
import { CardLarge } from './CardLarge';
import { ReauthFlow } from './ReauthFlow';
import { SourceSelect, buildPaySources, pickDefaultPaySource } from './SourceSelect';
import { ProgressButton } from './ProgressButton';
import { ChevronRight, WarningIcon } from './icons';
import { useMe } from '../../hooks/useMe';
import { useWallet } from '../../hooks/useWallet';
import { useTransfers } from '../../hooks/useTransfers';
import { useCardAccount, useCardTemplates } from '../../hooks/useCards';
import { useFeeQuote } from '../../hooks/useFeeQuote';
import {
  cardErrorMessage,
  cardPaySources,
  cardPayTo,
  checkCardEligibility,
  ensureCardAccount,
  fetchRecoverableCardIssue,
  isMastercardTemplate,
  issueCard,
  type CardTemplate,
  type RecoverableCardIssue,
} from '../../lib/cards';
import {
  previewCardPromo,
  promocodeErrorMessage,
  releaseCardPromo,
  type CardPromoPreview,
} from '../../lib/promocode';
import type { Chain, Token } from '../../lib/transfers';
import { countryFlag } from '../../lib/countries';
import { cardRechargeFeePct, cardRechargeMinUsd, cardSpendLimit } from '@incpt/shared';

const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2));

/** Swipe to pick a card → Issue → (register + approval if needed) → pay → apply. */
export function IssueCardFlow({
  onClose,
  onIssued,
}: {
  onClose: () => void;
  onIssued: () => void;
}) {
  const { t } = useTranslation();
  const { me, initData, canFetch } = useMe();
  const [activeIdx, setActiveIdx] = useState(0);
  const { templates, treasury } = useCardTemplates();
  const selected = templates[activeIdx] ?? null;
  // Registration is per-template and automatic — track the selected template's status.
  const { status: accStatus, rejectionMessage } = useCardAccount(selected?.templateId);
  const { wallet, balances } = useWallet();
  const { send } = useTransfers();
  const qc = useQueryClient();

  const [deposit, setDeposit] = useState('');
  const [sourceKey, setSourceKey] = useState<string | null>(null); // "TOKEN:CHAIN"
  const [payOpen, setPayOpen] = useState(false); // payment confirm sheet
  const [regOpen, setRegOpen] = useState(false); // approval-pending overlay
  const [reauthOpen, setReauthOpen] = useState(false);
  const reauthActionRef = useRef<'pay' | 'retry'>('pay');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // When payment succeeded but issueCard failed (provider busy), store the
  // transfer id so the user can retry the apply step without re-paying.
  const [paidTransferId, setPaidTransferId] = useState<string | null>(null);
  const [regBusy, setRegBusy] = useState(false); // auto-registration in flight
  const [regError, setRegError] = useState<string | null>(null);
  const [issueEmail, setIssueEmail] = useState('');
  const [emailOpen, setEmailOpen] = useState(false); // ask for email after "Issue"
  const [promoInput, setPromoInput] = useState('');
  const [promo, setPromo] = useState<CardPromoPreview | null>(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [recovery, setRecovery] = useState<RecoverableCardIssue | null>(null);
  const [retryReady, setRetryReady] = useState(true);
  const [termsLocked, setTermsLocked] = useState(false);
  const previewRequestRef = useRef(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const selectedTemplateIdRef = useRef<number | undefined>(selected?.templateId);
  selectedTemplateIdRef.current = selected?.templateId;
  const needsReauth = !!me?.pinSet || !!me?.biometricEnabled;
  const depositNum = Number(deposit) || 0;
  const depositOk = !selected?.requiresDeposit || depositNum >= (selected?.minDeposit ?? 0);
  const price = selected?.priceUsd ?? 0;
  // A promo code discounts the issue fee only — the deposit is always paid in full.
  const issuePrice = promo ? promo.discountedFeeUsd : price;
  const payTotal = issuePrice + (selected?.requiresDeposit ? depositNum : 0);
  // Nothing to pay → no transfer at all; the code alone funds the issue.
  const free = !!promo && payTotal === 0;
  const needsEmail =
    !!selected?.requiresEmail && (accStatus === 'none' || accStatus === 'rejected');
  const emailTrimmed = issueEmail.trim();
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed);

  // Only the chains the server gave a card destination for: a wallet on any
  // other chain cannot be paid from, and offering it ends in an "invalid
  // recipient" error that reads as the user's address being wrong.
  const sources = cardPaySources(buildPaySources(wallet, balances, t('nav.wallet')), treasury);
  // Default pay-from to the first USDT TRC-20 wallet (same as top-up).
  const source = sources.find((s) => s.key === sourceKey) ?? pickDefaultPaySource(sources);
  // The network fee (gas) is charged on top of payTotal from the same source, so
  // the balance must cover payTotal + fee or the treasury send fails as
  // "insufficient_balance" server-side.
  const feeTo = source ? cardPayTo(source.chain, treasury) : undefined;
  // The issue fee already clears the provider's apply fee, so the project pays
  // the network fee (and the one-time wallet warm-up) on this send. Quote it as a
  // card payment or the sheet would show — and reserve — a fee nobody charges.
  const cardPayment = selected
    ? ({
        kind: 'issue',
        templateId: selected.templateId,
        ...(promo ? { promoCode: promo.code } : {}),
        ...(selected.requiresDeposit ? { depositAmount: depositNum } : {}),
      } as const)
    : undefined;
  const {
    feeUsd: networkFee,
    maxFeeUsd,
    cardFee,
    fresh: feeFresh,
  } = useFeeQuote(
    source?.chain ?? 'trx',
    source?.token ?? 'USDT',
    feeTo ?? undefined,
    source?.address,
    cardPayment,
  );
  // Show the real fee; gate on the reserve (first Tron send may need headroom).
  // Paying before the quote is priced for THIS source would check the balance
  // against $0 and let the treasury send fail as insufficient_balance.
  const feeDisplay = free ? 0 : (networkFee ?? 0);
  const feeReserve = free ? 0 : (maxFeeUsd ?? feeDisplay);
  const totalWithFee = payTotal + feeDisplay;
  const totalReserved = payTotal + feeReserve;
  const canPay = free
    ? depositOk
    : !!source && depositOk && feeFresh && source.balance >= totalReserved;

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (!selected || !canFetch || !code || promoBusy) return;
    const templateId = selected.templateId;
    const requestId = ++previewRequestRef.current;
    setPromoBusy(true);
    setPromoError(null);
    try {
      const result = await previewCardPromo(
        initData,
        code,
        templateId,
        selected.requiresDeposit ? depositNum : undefined,
      );
      // A carousel change cannot install an absolute price quoted for another
      // template, even if that older request resolves last.
      if (requestId === previewRequestRef.current && selectedTemplateIdRef.current === templateId) {
        setPromo(result);
      }
    } catch (e) {
      if (requestId !== previewRequestRef.current) return;
      const msg = String((e as Error)?.message ?? e);
      setPromoError(
        msg.includes('wrong_kind')
          ? t('cards.promoWrongKind')
          : promocodeErrorMessage(msg, t, t('promo.errGeneric')),
      );
    } finally {
      if (requestId === previewRequestRef.current) setPromoBusy(false);
    }
  };

  // Default the first-deposit field to the template minimum so it's an actual
  // value (counted in "to pay") rather than an empty $0 behind a placeholder.
  useEffect(() => {
    setDeposit(selected?.requiresDeposit ? String(selected.minDeposit ?? '') : '');
  }, [selected?.templateId, selected?.requiresDeposit, selected?.minDeposit]);

  // Prefill the cardholder email once when switching to a requiresEmail template.
  useEffect(() => {
    if (!selected?.requiresEmail) {
      setIssueEmail('');
      return;
    }
    setIssueEmail(me?.email || me?.pendingEmail || '');
  }, [selected?.templateId, selected?.requiresEmail, me?.email, me?.pendingEmail]);

  const scrollToCard = (i: number) => {
    if (termsLocked) return;
    const el = carouselRef.current;
    const child = el?.children[i] as HTMLElement | undefined;
    if (!el || !child) return;
    const left =
      child.getBoundingClientRect().left -
      el.getBoundingClientRect().left +
      el.scrollLeft -
      (el.clientWidth - child.offsetWidth) / 2;
    el.scrollTo({ left, behavior: 'smooth' });
    setActiveIdx(i);
  };

  // iOS (Telegram WKWebView) does not deliver a horizontal pan to an overflow-x
  // scroller nested in this sheet — the client takes the swipe. Android scrolls
  // natively, so only iOS tracks the finger and moves scrollLeft itself.
  useEffect(() => {
    const el = carouselRef.current;
    if (!el || termsLocked) return;
    const platform = window.Telegram?.WebApp?.platform;
    const ios =
      platform === 'ios' ||
      (!platform && /iPhone|iPad|iPod/i.test(navigator.userAgent));
    if (!ios) return;

    const prevTouchAction = el.style.touchAction;
    el.style.touchAction = 'pan-y';

    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let startIndex = 0;
    let axis: 'x' | 'y' | null = null;

    const nearest = () => {
      const center = el.scrollLeft + el.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      Array.from(el.children).forEach((ch, i) => {
        const c = ch as HTMLElement;
        const mid =
          c.getBoundingClientRect().left -
          el.getBoundingClientRect().left +
          el.scrollLeft +
          c.offsetWidth / 2;
        const d = Math.abs(mid - center);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      });
      return best;
    };
    const centerChild = (i: number) => {
      const child = el.children[i] as HTMLElement | undefined;
      if (!child) return;
      const left =
        child.getBoundingClientRect().left -
        el.getBoundingClientRect().left +
        el.scrollLeft -
        (el.clientWidth - child.offsetWidth) / 2;
      el.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    };

    const onStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (e.touches.length !== 1 || !t) return;
      startX = t.clientX;
      startY = t.clientY;
      startLeft = el.scrollLeft;
      startIndex = nearest();
      axis = null;
    };
    const onMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (axis === 'y' || e.touches.length !== 1 || !t) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (!axis) {
        // Claim a sideways lean immediately. Telegram iOS takes the swipe if
        // the first touchmoves are left uncancelled.
        if (Math.abs(dx) > Math.abs(dy) && dx !== 0) e.preventDefault();
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (axis === 'x') el.style.scrollSnapType = 'none';
      }
      if (axis !== 'x') return;
      e.preventDefault();
      el.scrollLeft = startLeft - dx;
    };
    const onEnd = () => {
      el.style.scrollSnapType = '';
      if (axis === 'x') {
        const delta = el.scrollLeft - startLeft;
        const dir = delta > 28 ? 1 : delta < -28 ? -1 : 0;
        const i = Math.max(0, Math.min(el.childElementCount - 1, startIndex + dir));
        centerChild(i);
      }
      axis = null;
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.style.touchAction = prevTouchAction;
      el.style.scrollSnapType = '';
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [termsLocked]);

  // Track the centred card in the carousel.
  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (termsLocked) return;
    const el = e.currentTarget;
    const center = el.scrollLeft + el.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    Array.from(el.children).forEach((ch, i) => {
      const c = (ch as HTMLElement).offsetLeft + (ch as HTMLElement).offsetWidth / 2;
      const d = Math.abs(c - center);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    setActiveIdx(best);
  };

  // Stable idempotency keys per issue-attempt, reused across retries (reauth, a
  // lost response) so a retry never double-charges or re-rents energy. Reset when
  // the template / amount / source changes — that's a genuinely new attempt.
  const payIdemRef = useRef<string | null>(null);
  const issueIdemRef = useRef<string | null>(null);
  // Mirrors paidTransferId for code that must read it without a stale closure
  // (the reset effect below, and the double-charge guards in submit).
  const paidTransferIdRef = useRef<string | null>(null);
  const submittingRef = useRef(false);
  const markPaid = (id: string | null) => {
    paidTransferIdRef.current = id;
    setPaidTransferId(id);
    if (id) setTermsLocked(true);
  };
  useEffect(() => {
    // Once the payment landed, this attempt is bound to that transfer: a form
    // change must NOT swap "Retry issue" back to "Pay & issue" — that would
    // charge the user a second time for the same unissued card.
    if (paidTransferIdRef.current) return;
    payIdemRef.current = null;
    issueIdemRef.current = null;
    setError(null);
  }, [selected?.templateId, payTotal, sourceKey]);

  // The preview is priced per template, so a different card needs a fresh one.
  useEffect(() => {
    if (paidTransferIdRef.current) return;
    previewRequestRef.current += 1;
    setPromo(null);
    setPromoError(null);
  }, [selected?.templateId]);

  // Recover the latest paid/reserved apply after Telegram reloads. Hydration is
  // server-driven, so the exact template, deposit and promo terms are restored.
  useEffect(() => {
    if (!canFetch) return;
    let cancelled = false;
    void fetchRecoverableCardIssue(initData)
      .then(({ intent }) => {
        if (!cancelled) setRecovery(intent);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [canFetch, initData]);

  useEffect(() => {
    const retryAt = recovery?.processingRetryAt
      ? new Date(recovery.processingRetryAt).getTime()
      : 0;
    const waitMs = retryAt - Date.now();
    if (waitMs <= 0) {
      setRetryReady(true);
      return;
    }
    setRetryReady(false);
    const timer = window.setTimeout(() => {
      setRetryReady(true);
      void fetchRecoverableCardIssue(initData)
        .then(({ intent }) => setRecovery(intent))
        .catch(() => {});
      void qc.invalidateQueries({ queryKey: ['cards'] });
    }, waitMs);
    return () => window.clearTimeout(timer);
  }, [initData, qc, recovery?.processingRetryAt]);

  const recoveryHydratedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!recovery || recoveryHydratedRef.current === recovery.id || templates.length === 0) return;
    const idx = templates.findIndex((tpl) => tpl.templateId === recovery.templateId);
    if (idx < 0) return;
    if (activeIdx !== idx) {
      setActiveIdx(idx);
      return;
    }
    recoveryHydratedRef.current = recovery.id;
    setTermsLocked(true);
    setDeposit(recovery.depositAmount > 0 ? String(recovery.depositAmount) : '');
    if (recovery.promoCode) {
      setPromoInput(recovery.promoCode);
      setPromo({
        code: recovery.promoCode,
        discountPercent: recovery.discountPercent,
        issueFeeUsd: recovery.baseFeeUsd,
        discountedFeeUsd: Math.round(recovery.baseFeeUsd * (100 - recovery.discountPercent)) / 100,
      });
    }
    markPaid(recovery.paymentTransferId);
    setError(t('cards.issueRecovery'));
    setPayOpen(true);
  }, [activeIdx, recovery, t, templates]);

  /** Call issueCard and handle success/failure without re-paying. A null
   *  transfer is a fully promo-funded issue. */
  const applyCard = async (transferId: string | null, token?: string) => {
    if (!selected || !canFetch) return;
    if (!issueIdemRef.current) issueIdemRef.current = crypto.randomUUID();
    const res = await issueCard(
      initData,
      {
        template_id: selected.templateId,
        ...(selected.requiresDeposit ? { deposit_amount: depositNum } : {}),
        ...(transferId ? { payment_transfer_id: transferId } : {}),
        ...(promo ? { promo_code: promo.code } : {}),
      },
      issueIdemRef.current,
      token,
    );
    await qc.invalidateQueries({ queryKey: ['cards'] });
    // An idempotency replay also answers 200 — including a previously failed or
    // still-running apply that has no card. Only an actual card row is success;
    // anything else must keep the retry UI instead of reporting "card issued".
    if (!res.card) throw new Error(`issue_not_completed:${res.status}`);
    // The Banana user is now attached to the issued card (1 user = 1 card); a
    // fresh issue must re-register, so drop the cached approved account status.
    await qc.invalidateQueries({ queryKey: ['card-account', selected.templateId] });
    onIssued();
  };

  /** Retry the apply step only — payment already succeeded. New idempotency key. */
  const retryApply = async (token?: string) => {
    const transferId = paidTransferIdRef.current;
    if (!transferId || submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    setError(null);
    issueIdemRef.current = crypto.randomUUID(); // fresh key so backend doesn't replay failed attempt
    try {
      await applyCard(transferId, token);
    } catch (e) {
      const msg = String((e as Error)?.message ?? e);
      setError(cardErrorMessage(msg, t, t('cards.issueFailed'), 'issue'));
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };

  /** Promo covers everything: no transfer, straight to apply. Moves no money,
   *  so a failed attempt simply runs again with a fresh idempotency key — the
   *  server resumes the failed intent bound to the code. */
  const submitFree = async (token?: string) => {
    if (!selected || !canFetch || submittingRef.current) return;
    submittingRef.current = true;
    setTermsLocked(true);
    setBusy(true);
    setError(null);
    try {
      await checkCardEligibility(initData, selected.templateId);
      issueIdemRef.current = crypto.randomUUID();
      await applyCard(null, token);
    } catch (e) {
      const msg = String((e as Error)?.message ?? e);
      setError(cardErrorMessage(msg, t, t('cards.issueFailed'), 'issue'));
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };

  const submit = async (token?: string) => {
    if (free && !paidTransferIdRef.current) return submitFree(token);
    if (!selected || !canFetch || !wallet?.id || !source) return;
    // Already paid for this attempt → finish the apply step, never pay again.
    if (paidTransferIdRef.current) return retryApply(token);
    // busy/disabled only applies after a re-render, so a fast double tap can
    // reach this twice; the ref closes that window synchronously.
    if (submittingRef.current) return;
    submittingRef.current = true;
    // Freeze template/deposit/source before the wallet starts signing. The
    // eventual intent and any retry must use exactly these paid terms.
    setTermsLocked(true);
    setBusy(true);
    setError(null);
    try {
      const to = cardPayTo(source.chain, treasury);
      if (!to) throw new Error('card fee treasury not configured');
      // Verify the user can actually be issued this card BEFORE charging — never
      // take payment for a card that would fail to issue (account not approved,
      // template unavailable). Throws → caught below, no charge.
      await checkCardEligibility(initData, selected.templateId);
      if (!payIdemRef.current) payIdemRef.current = crypto.randomUUID();
      if (!issueIdemRef.current) issueIdemRef.current = crypto.randomUUID();
      const transfer = await send.mutateAsync({
        walletId: wallet.id,
        chain: source.chain,
        token: source.token,
        to,
        amount: String(payTotal),
        reauthToken: token,
        fromAddressId: source.addressId,
        fromIndex: source.fromIndex,
        idempotencyKey: payIdemRef.current,
        // Same as top-up: issue verifies the payment transfer is settled in DB.
        awaitSettlement: true,
        // Re-verified server-side (treasury recipient + offered template) — this
        // is what makes the server charge $0 network fee, matching the quote.
        cardPayment: {
          kind: 'issue',
          templateId: selected.templateId,
          ...(promo ? { promoCode: promo.code } : {}),
          ...(selected.requiresDeposit ? { depositAmount: depositNum } : {}),
        },
      });
      // Payment succeeded — remember it so the user can retry apply without re-paying.
      markPaid(transfer.id);
      await applyCard(transfer.id, token);
    } catch (e) {
      const msg = String((e as Error)?.message ?? e);
      if (
        !paidTransferIdRef.current &&
        !/transfer_verification_pending/i.test(msg) &&
        /sponsored_resources_expired|preparation_lease_expired|transfer_failed|energy_rental_failed|sponsor_risk_limit/i.test(
          msg,
        )
      ) {
        payIdemRef.current = null;
      }
      if (!paidTransferIdRef.current && promo) {
        const released = await releaseCardPromo(initData, promo.code).catch(() => ({
          released: false,
        }));
        if (released.released) {
          setTermsLocked(false);
          setPromo(null);
        } else {
          void fetchRecoverableCardIssue(initData)
            .then(({ intent }) => setRecovery(intent))
            .catch(() => {});
        }
      }
      setError(cardErrorMessage(msg, t, t('cards.issueFailed'), 'issue'));
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };

  // From "Issue for $X": approved → payment; needs email → email step; else register.
  const onIssueTap = () => {
    if (!selected || regBusy) return;
    if (selected.poolAvailable || accStatus === 'approved') {
      setPayOpen(true);
      return;
    }
    if (needsEmail) {
      setEmailOpen(true);
      return;
    }
    void onIssue();
  };

  const onEmailContinue = () => {
    if (!emailOk) return;
    setEmailOpen(false);
    void onIssue(accStatus === 'rejected');
  };

  // Register a Banana user for this template (with email when required) and wait
  // for approval, then open payment.
  const onIssue = async (forceReregister = false) => {
    if (!selected || !canFetch || regBusy) return; // guard double-taps → dup registration
    if (!forceReregister && accStatus === 'approved') {
      setPayOpen(true);
      return;
    }
    // Email is only required to create / resubmit a registration — not while
    // PENDING (Banana user already exists; email is fixed until REJECTED).
    const mustEnterEmail =
      !!selected.requiresEmail &&
      (forceReregister || accStatus === 'none' || accStatus === 'rejected');
    if (mustEnterEmail && !emailOk) {
      setEmailOpen(true);
      return;
    }
    setRegError(null);
    setRegOpen(true);
    setRegBusy(true);
    try {
      const res = await ensureCardAccount(initData, selected.templateId, {
        waitForApproval: true,
        forceReregister,
        ...(mustEnterEmail ? { email: emailTrimmed } : {}),
      });
      await qc.invalidateQueries({ queryKey: ['card-account', selected.templateId] });
      if (res.status === 'approved') {
        setRegOpen(false);
        setPayOpen(true);
      } else if (res.status === 'rejected') {
        setRegError(res.rejectionMessage || t('cards.approvalRejected'));
      }
      // pending → the overlay stays; useCardAccount polls and the effect below
      // moves to payment / surfaces REJECTED with Banana's message.
    } catch (e) {
      setRegError(
        cardErrorMessage(String((e as Error)?.message ?? e), t, t('cards.registerFailed')),
      );
    } finally {
      setRegBusy(false);
    }
  };

  const onPayConfirm = () => {
    if (!canPay) return;
    reauthActionRef.current = 'pay';
    if (needsReauth) setReauthOpen(true);
    else void submit();
  };

  // POST /v1/cards is reauth-gated, so the retry needs a fresh token too —
  // without it PIN/biometric users only ever get 401 on "Retry issue".
  const onRetryConfirm = () => {
    reauthActionRef.current = 'retry';
    if (needsReauth) setReauthOpen(true);
    else void retryApply();
  };

  // React to webhook/provider status while the approval overlay is open.
  useEffect(() => {
    if (!regOpen) return;
    if (accStatus === 'approved') {
      setRegOpen(false);
      setPayOpen(true);
    } else if (accStatus === 'rejected') {
      setRegError(rejectionMessage || t('cards.approvalRejected'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regOpen, accStatus, rejectionMessage]);

  // Never leave the user on an endless spinner if Banana stays pending or a
  // webhook/provider read is delayed. Polling continues, so a later terminal
  // status still replaces this message or advances to payment.
  useEffect(() => {
    if (!regOpen || regError || accStatus !== 'pending') return;
    const timer = window.setTimeout(() => {
      setRegError(t('cards.errNotApproved'));
    }, 45_000);
    return () => window.clearTimeout(timer);
  }, [accStatus, regError, regOpen, t]);

  const lim = (n: number | undefined, key: 'perTx' | 'perDay' | 'perMonth') =>
    `${cardSpendLimit(n, key).toLocaleString('en-US')} USDT`;

  return (
    <>
      {/* Select-a-card action sheet */}
      <BottomSheet open={!payOpen && !regOpen && !emailOpen} onClose={onClose}>
        <div className="-mx-4 flex flex-col">
          {/* Swipeable card previews. overflow-y-hidden: overflow-x:auto alone
              computes y to auto, and iOS then won't pan x inside the sheet. */}
          <div
            ref={carouselRef}
            onScroll={onScroll}
            onPointerDown={(e) => e.stopPropagation()}
            className={`no-scrollbar flex w-full min-w-0 gap-3 px-4 pt-2 ${
              termsLocked
                ? 'overflow-hidden'
                : 'touch-pan-x overflow-x-auto overflow-y-hidden overscroll-x-contain [scroll-snap-type:x_mandatory]'
            }`}
          >
            {templates.map((tpl) => (
              <div
                key={tpl.templateId}
                className="w-[calc(100%-2rem)] shrink-0 [scroll-snap-align:center]"
              >
                <CardLarge
                  last4="••••"
                  balance=""
                  expiry=""
                  mastercard={isMastercardTemplate(tpl.templateId)}
                />
              </div>
            ))}
          </div>

          {/* Dots — also a tap target, for clients where the swipe never lands. */}
          {templates.length > 1 && (
            <div className="mt-3 flex justify-center gap-1.5">
              {templates.map((tpl, i) => (
                <button
                  key={tpl.templateId}
                  type="button"
                  aria-current={i === activeIdx ? 'true' : undefined}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => scrollToCard(i)}
                  className="flex h-6 items-center px-0.5"
                >
                  <span
                    className={`h-1.5 rounded-full transition-all ${
                      i === activeIdx ? 'w-4 bg-ink' : 'w-1.5 bg-black/20'
                    }`}
                  />
                </button>
              ))}
            </div>
          )}

          <div className="px-4">
            <h2 className="mt-5 text-center text-[18px] font-semibold text-ink">
              {selected?.name ?? ''}
            </h2>
            <p className="mt-1.5 text-center text-[13px] leading-[1.5] text-muted">
              {t('cards.selectDesc')}
            </p>

            {/* Pay methods — per-template flags from /templates */}
            <div className="mt-5">
              <CheckRow label="Apple Pay" supported={selected?.applePay !== false} />
              <div className="h-px bg-black/[0.06]" />
              <CheckRow label="Google Pay" supported={selected?.googlePay !== false} />
              {selected?.worksInUkraine && (
                <>
                  <div className="h-px bg-black/[0.06]" />
                  <CheckRow label={t('cardSettings.worksInUkraine')} supported />
                </>
              )}
            </div>

            {/* Details — live from the selected template */}
            <div className="mt-3 rounded-[20px] bg-[#f6f7f8] px-4 py-2.5">
              {selected?.countryIso && selected?.country && (
                <DetailRow
                  label={t('cardSettings.issuerJurisdiction')}
                  value={`${countryFlag(selected.countryIso)} ${selected.country}`}
                />
              )}
              <DetailRow label={t('cardSettings.kyc')} value={t('cardSettings.kycValue')} />
              <DetailRow
                label={t('cardSettings.topUpFee')}
                value={`${cardRechargeFeePct(selected?.rechargeFee, selected?.templateId)}%`}
              />
              <DetailRow
                label={t('cardSettings.minTopUp')}
                value={`${cardRechargeMinUsd(selected?.rechargeMin)} USDT`}
              />
              <DetailRow
                label={t('cardSettings.txLimit')}
                value={lim(selected?.limitPerTx, 'perTx')}
              />
              <DetailRow
                label={t('cardSettings.dailyLimit')}
                value={lim(selected?.limitPerDay, 'perDay')}
              />
              <DetailRow
                label={t('cardSettings.monthlyLimit')}
                value={lim(selected?.limitPerMonth, 'perMonth')}
              />
            </div>
          </div>

          {/* Issue CTA — pinned to the sheet bottom so it stays reachable while
              the card details scroll underneath. */}
          <div className="sticky bottom-0 z-10 -mb-[calc(var(--tg-safe-bottom)_+_2rem)] mt-5 px-4 pb-[calc(var(--tg-safe-bottom)_+_1rem)] pt-3">
            <button
              onClick={onIssueTap}
              disabled={!selected || regBusy}
              className="flex h-14 w-full items-center justify-center rounded-full bg-ink text-[15px] font-medium text-white active:opacity-90 disabled:opacity-40"
            >
              {t('cards.issueNow')} · ${fmt(price)}
            </button>
          </div>
        </div>
      </BottomSheet>

      {/* Email step — mount only after Issue, so a closed sheet can't steal focus */}
      {emailOpen && (
        <BottomSheet open onClose={() => setEmailOpen(false)} title={t('cards.emailLabel')}>
          <p className="mt-2 text-[14px] leading-[1.45] text-muted">{t('cards.emailForCard')}</p>
          <input
            id="card-issue-email"
            type="email"
            autoComplete="email"
            inputMode="email"
            value={issueEmail}
            onChange={(e) => setIssueEmail(e.target.value)}
            placeholder="name@example.com"
            className="mt-4 h-12 w-full rounded-2xl bg-[#f6f7f8] px-4 text-[15px] text-ink outline-none placeholder:text-muted"
          />
          {emailTrimmed.length > 0 && !emailOk && (
            <p className="mt-1.5 text-[12px] text-[#ff3b30]">{t('cards.emailInvalid')}</p>
          )}
          <button
            onClick={onEmailContinue}
            disabled={!emailOk || regBusy}
            className="mt-6 flex h-14 w-full items-center justify-center rounded-full bg-ink text-[15px] font-medium text-white active:opacity-90 disabled:opacity-40"
          >
            {t('cards.register')}
          </button>
        </BottomSheet>
      )}

      {/* Payment confirm sheet (source wallet + deposit) */}
      <BottomSheet open={payOpen} onClose={() => setPayOpen(false)} title={selected?.name ?? ''}>
        {selected && (
          <div className="mt-4 flex flex-col">
            <Row label={t('cards.applyFee')} value={`$${fmt(price)}`} />
            {promo && (
              <div className="flex items-center justify-between border-b border-black/[0.06] py-3">
                <span className="text-[14px] text-muted">
                  {t('cards.promoApplied', { code: promo.code })}
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-[15px] font-medium text-[#34c759]">
                    {promo.discountPercent >= 100
                      ? t('cards.promoFreeIssue')
                      : `−$${fmt(price - promo.discountedFeeUsd)}`}
                  </span>
                  {!termsLocked && (
                    <button
                      onClick={() => {
                        void releaseCardPromo(initData, promo.code).catch(() => {});
                        setPromo(null);
                        setPromoInput('');
                      }}
                      disabled={busy}
                      className="text-[13px] text-muted active:opacity-60"
                    >
                      {t('cards.promoRemove')}
                    </button>
                  )}
                </span>
              </div>
            )}
            <Row
              label={t('cards.rechargeFee')}
              value={`${cardRechargeFeePct(selected.rechargeFee, selected.templateId)}%`}
            />
            {free ? null : cardFee?.kind === 'issue' && feeDisplay === 0 ? (
              <Row label={t('wallet.txCommission')} value={t('cards.networkFree')} accent />
            ) : networkFee != null && networkFee > 0 ? (
              <Row label={t('wallet.txCommission')} value={`$${fmt(networkFee)}`} />
            ) : null}
            {selected.requiresDeposit && (
              <div className="border-b border-black/[0.06] py-3">
                <div className="flex items-center justify-between">
                  <span className="text-[14px] text-muted">{t('cards.deposit')}</span>
                  <div className="flex items-center justify-end">
                    <span className="text-[15px] font-medium text-ink">$</span>
                    <input
                      value={deposit}
                      onChange={(e) => setDeposit(e.target.value.replace(/[^\d.]/g, ''))}
                      // Locked once paid: the amount is already settled on-chain
                      // and the pending apply must use exactly that value.
                      disabled={
                        termsLocked || selected.templateId === 32 || selected.templateId === 27
                      }
                      inputMode="decimal"
                      placeholder={String(selected.minDeposit)}
                      size={1}
                      className="min-w-[1.5ch] max-w-[6ch] bg-transparent text-right text-[15px] font-medium text-ink outline-none placeholder:text-muted"
                      style={{
                        width: `${Math.max(1, (deposit || String(selected.minDeposit)).length)}ch`,
                      }}
                    />
                  </div>
                </div>
                <p className="mt-1 text-right text-[12px] text-muted">
                  {t('cards.depositHint', { min: selected.minDeposit, currency: 'USD' })}
                </p>
              </div>
            )}
            <Row label={t('cards.youPay')} value={`$${fmt(totalWithFee)}`} accent />

            {!promo && !termsLocked && (
              <div className="mt-4">
                <div className="flex gap-2">
                  <input
                    value={promoInput}
                    onChange={(e) => {
                      setPromoInput(e.target.value);
                      setPromoError(null);
                    }}
                    placeholder={t('cards.promoPlaceholder')}
                    autoComplete="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    className="h-11 min-w-0 flex-1 rounded-2xl bg-[#f6f7f8] px-4 text-[15px] uppercase text-ink outline-none placeholder:normal-case placeholder:text-muted"
                  />
                  <button
                    onClick={() => void applyPromo()}
                    disabled={!promoInput.trim() || promoBusy || busy}
                    className="h-11 shrink-0 rounded-2xl bg-ink px-4 text-[14px] font-medium text-white active:opacity-90 disabled:opacity-40"
                  >
                    {promoBusy ? '…' : t('cards.promoApply')}
                  </button>
                </div>
                {promoError && <p className="mt-1.5 text-[12px] text-[#ff3b30]">{promoError}</p>}
              </div>
            )}

            {!free && (
              <div className="mt-4">
                <span className="text-[13px] text-muted">{t('cards.payFrom')}</span>
                {sources.length === 0 ? (
                  <p className="mt-2 flex items-center gap-1.5 text-[13px] font-medium text-[#b25e00]">
                    <WarningIcon size={15} />
                    {t('cards.noStablecoin')}
                  </p>
                ) : (
                  <div className="mt-2">
                    <SourceSelect
                      sources={sources}
                      value={source?.key ?? null}
                      // Same reason as the deposit field: the paid transfer already
                      // came from this wallet, so it can't be swapped mid-attempt.
                      onChange={(key) => {
                        if (!termsLocked) setSourceKey(key);
                      }}
                      required={payTotal}
                    />
                  </div>
                )}
                {!busy && source && source.balance < totalReserved && (
                  <p className="mt-2 flex items-center gap-1.5 text-[13px] font-medium text-[#b25e00]">
                    <WarningIcon size={15} />
                    {t('cards.insufficientForFee', { amount: fmt(totalReserved) })}
                  </p>
                )}
              </div>
            )}

            {error && (
              <div className="mt-3 rounded-2xl bg-[#fff3f2] p-3">
                <p className="flex items-start gap-1.5 text-[13px] font-medium text-[#ff3b30]">
                  <WarningIcon size={15} className="mt-[1px] shrink-0" />
                  {error}
                </p>
                {paidTransferId && (
                  <p className="mt-1.5 text-[12px] leading-[1.5] text-[#ff3b30]/70">
                    {t('cards.paymentSafe')}
                  </p>
                )}
              </div>
            )}

            {paidTransferId && error ? (
              <ProgressButton
                active={busy}
                onClick={onRetryConfirm}
                disabled={busy || !retryReady}
                expectedMs={12_000}
                className={`mt-6 flex h-14 items-center justify-center rounded-full bg-ink text-[15px] font-medium text-white ${
                  busy || !retryReady ? 'pointer-events-none opacity-40' : 'active:opacity-90'
                }`}
              >
                {busy ? t('cards.paying') : t('cards.retryIssue')}
              </ProgressButton>
            ) : (
              <ProgressButton
                active={busy}
                onClick={onPayConfirm}
                disabled={busy || !canPay || !retryReady}
                expectedMs={30_000}
                className={`mt-6 flex h-14 items-center justify-center rounded-full bg-ink text-[15px] font-medium text-white ${
                  busy || !canPay || !retryReady
                    ? 'pointer-events-none opacity-40'
                    : 'active:opacity-90'
                }`}
              >
                {busy
                  ? t('cards.paying')
                  : free
                    ? t('cards.issueFree')
                    : `${t('cards.payAndIssue')} · $${fmt(totalWithFee)}`}
              </ProgressButton>
            )}
          </div>
        )}
      </BottomSheet>

      {/* Approval-pending overlay (registration is automatic — no form) */}
      {regOpen && (
        <div className="push-enter fixed inset-0 z-[60] overflow-y-auto bg-holo">
          <ScreenBackdrop full />
          <div className="relative z-10 flex h-full min-h-full flex-col px-4 pb-[max(1.5rem,var(--tg-safe-bottom))] pt-[calc(var(--tg-safe-top)_+_0.75rem)]">
            <div className="relative flex h-10 items-center justify-center">
              <button
                onClick={() => setRegOpen(false)}
                className="absolute left-0 flex h-9 w-9 items-center justify-center rounded-full bg-black/[0.06] text-ink"
                aria-label="Back"
              >
                <ChevronRight size={18} className="rotate-180 text-ink" />
              </button>
              <span className="text-[16px] font-medium text-ink">{t('cards.registerTitle')}</span>
            </div>

            {regError ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
                <WarningIcon size={28} />
                <span className="text-[16px] font-medium text-ink">
                  {t('cards.approvalRejected')}
                </span>
                <span className="text-[14px] leading-[1.45] text-muted">{regError}</span>
                {selected?.requiresEmail && (
                  <input
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    value={issueEmail}
                    onChange={(e) => setIssueEmail(e.target.value)}
                    placeholder="name@example.com"
                    className="mt-1 h-12 w-full max-w-[280px] rounded-2xl bg-[#f6f7f8] px-4 text-left text-[15px] text-ink outline-none placeholder:text-muted"
                  />
                )}
                <button
                  onClick={() => void onIssue(true)}
                  disabled={regBusy || (selected?.requiresEmail && !emailOk)}
                  className="mt-2 flex h-12 w-full max-w-[280px] items-center justify-center rounded-full bg-ink text-[15px] font-medium text-white active:opacity-90 disabled:opacity-40"
                >
                  {t('common.retry')}
                </button>
              </div>
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 pt-20 text-center">
                <span className="h-7 w-7 animate-spin rounded-full border-2 border-ink/15 border-t-ink" />
                <div className="flex flex-col gap-1">
                  <span className="text-[16px] font-medium text-ink">
                    {t('cards.approvalPending')}
                  </span>
                  <span className="px-6 text-[14px] text-muted">
                    {t('cards.approvalPendingDesc')}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {reauthOpen && canFetch && (
        <div className="push-enter fixed inset-0 z-[70] overflow-y-auto bg-holo">
          <ReauthFlow
            initData={initData}
            biometricEnabled={!!me?.biometricEnabled}
            onSuccess={(token) => {
              setReauthOpen(false);
              if (reauthActionRef.current === 'retry') void retryApply(token);
              else void submit(token);
            }}
            onCancel={() => setReauthOpen(false)}
          />
        </div>
      )}
    </>
  );
}

function Row({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex items-center justify-between border-b border-black/[0.06] py-3 last:border-b-0">
      <span className="text-[14px] text-muted">{label}</span>
      <span className={`text-[15px] font-medium ${accent ? 'text-[#377dff]' : 'text-ink'}`}>
        {value}
      </span>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <span className="text-[14px] text-muted">{label}</span>
      <span className="text-[14px] font-medium text-ink">{value}</span>
    </div>
  );
}

function CheckRow({ label, supported = true }: { label: string; supported?: boolean }) {
  return (
    <div className="flex items-center justify-between py-3">
      <span className={`text-[16px] ${supported ? 'text-ink' : 'text-muted'}`}>{label}</span>
      {supported ? (
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#34c759]">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path
              d="M5 12.5l4.5 4.5L19 7"
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      ) : (
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-black/10">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              className="text-muted"
            />
          </svg>
        </span>
      )}
    </div>
  );
}
